// Prefetch + SWR cache for the LogJob customer + per-customer pricing pair.
// Mirrors the pattern in useLogJobHistory so:
//   - clicking "Log Job" on the dashboard kicks off the customer + pricing
//     fetch immediately (in parallel with the history prefetch already wired)
//   - by the time LogJob mounts, the data is either ready (cache hit) or
//     in-flight (the mount await joins the same promise, no duplicate query)
//   - signOut / sign-in invalidates the cache via auth state listener
//
// Closes the staggered-render gap the user noticed: the customer + pricing
// fetch was the LAST piece to arrive, holding back the form.

import { supabase } from '../supabase';
import { fieldReadTablesForRole, getFieldReadTables } from '../utils/job/fieldReadTables';
import { canCurrentSessionSeePrices } from '../utils/auth/priceVisibilityStore';

// Map<key, { customer, pricing }>
const customerCache = new Map();
// Map<key, Promise<{ customer, pricing }>>
const inflightPromises = new Map();
// Bumped per customer when their lifecycle status changes, so a fetch that
// started while the pool was still Closed cannot write that row back after
// the status has moved.
const customerGeneration = new Map();
// Bumped on signOut / sign-in so a stale in-flight fetch from a prior session
// cannot land its result into the cache after the new session has started.
let cacheGeneration = 0;

// RD-ROLE-005: a price-blind field role never needs `customer_pricing` — the
// key carries a 'p'/'n' suffix so a warm cache from a price-visible context
// (or the default-true route-board prefetch below) never silently hands a
// blind caller pricing it shouldn't have joined, and vice versa.
// The key carries the TABLE actually read: a base-`customers` read that RLS
// empties for a blind field role (0 rows -> PGRST116) is cached as a failure,
// and without the table in the key that failure would alias the later
// `field_customers` read for the same customer (the page's own first load
// runs before `role` resolves, so it can BE that base read).
const cacheKey = (tenantId, customerId, withPricing, table) => `${tenantId}:${customerId}:${withPricing ? 'p' : 'n'}:${table}`;

/**
 * Flattens the FK-embedded assigned-worker profile into a top-level
 * `assigned_worker_name` (and drops the nested object) so LogJob's attribution
 * pill can render the worker's name on the FIRST frame — before the async
 * tenant-workers list arrives. RLS still governs the embedded read: if the
 * caller can't see that profile, it comes back null and we degrade gracefully.
 */
export function mergeAssignedWorkerName(customerRow) {
  if (!customerRow) return null;
  const { assigned_worker_profile, ...rest } = customerRow;
  return { ...rest, assigned_worker_name: assigned_worker_profile?.full_name || null };
}

/**
 * RD-ROLE-005b (P0-B) — the base `customers` SELECT returns 0 rows to a
 * blind field role (worker/supervisor with canViewPrices() === false), and
 * PostgREST embeds don't resolve through a DEFINER view — so the blind path
 * reads `field_customers` with a plain `select('*')` and fetches the
 * assigned-worker's name as a SECOND query on `profiles`.
 * `profiles_select_self_or_same_tenant` lets any same-tenant member read a
 * coworker's profile, so this also resolves for the covering-worker case
 * (the customer's `assigned_worker` is a DIFFERENT worker than the caller).
 * `role` is optional — the fire-and-forget prefetch call sites (route-board
 * cards) don't know the viewer's role at prefetch time and keep the default
 * (BASE_TABLES via `fieldReadTablesForRole(undefined, withPricing)`); a
 * genuinely blind mount still resolves correctly via its OWN
 * `loadLogJobCustomer` call (different cache key — see `cacheKey` above).
 */
function dropCustomerCache(customerId) {
  const id = String(customerId);
  const marker = `:${id}:`;
  for (const key of customerCache.keys()) {
    if (key.includes(marker)) customerCache.delete(key);
  }
  for (const key of inflightPromises.keys()) {
    if (key.includes(marker)) inflightPromises.delete(key);
  }
}

/** Drop every cached Log Job row for this customer. Call after a lifecycle change. */
export function invalidateLogJobCustomerCache(customerId) {
  if (!customerId) return;
  customerGeneration.set(String(customerId), (customerGeneration.get(String(customerId)) || 0) + 1);
  dropCustomerCache(customerId);
}

function loadCustomerOnce(tenantId, customerId, withPricing, role, fresh) {
  if (!tenantId || !customerId) return Promise.resolve({ customer: null, pricing: [] });
  // No role known (fire-and-forget route-board prefetch, or the page's first
  // render before AuthContext resolves) -> ask the STORE, which mirrors the
  // session's role + price visibility, so the prefetch and the page read the
  // SAME table for a blind worker instead of a base table that RLS empties.
  const fieldTables = role == null ? getFieldReadTables() : fieldReadTablesForRole(role, withPricing);
  const key = cacheKey(tenantId, customerId, withPricing, fieldTables.customers);
  // A Log Job open re-reads the row. The cached copy can still say Closed
  // after the pool was moved to Opening, and that is what picks the form.
  if (!fresh) {
    if (customerCache.has(key)) return Promise.resolve(customerCache.get(key));
    if (inflightPromises.has(key)) return inflightPromises.get(key);
  }
  const startedGen = cacheGeneration;
  const startedCustomerGen = customerGeneration.get(String(customerId)) || 0;
  const blind = fieldTables.customers === 'field_customers';
  const promise = (async () => {
    try {
      const [
        { data: customerData, error: customerError },
        { data: pricingData, error: pricingError },
      ] = await Promise.all([
        blind
          ? supabase.from(fieldTables.customers).select('*').eq('id', customerId).eq('tenant_id', tenantId).single()
          : supabase
              .from('customers')
              .select('*, assigned_worker_profile:profiles!customers_assigned_worker_fkey(full_name)')
              .eq('id', customerId)
              .eq('tenant_id', tenantId)
              .single(),
        // RD-ROLE-005: a price-blind field role has no RLS access to a
        // useful `customer_pricing` row anyway — skip the fetch entirely
        // rather than let it come back empty-by-policy.
        withPricing
          ? supabase.from('customer_pricing').select('*').eq('customer_id', customerId).eq('tenant_id', tenantId)
          : Promise.resolve({ data: [], error: null }),
      ]);

      let customerRow = customerError ? null : (customerData || null);
      if (blind && customerRow && customerRow.assigned_worker) {
        const { data: workerProfile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', customerRow.assigned_worker)
          .maybeSingle();
        customerRow = {
          ...customerRow,
          assigned_worker_profile: workerProfile ? { full_name: workerProfile.full_name } : null,
        };
      }

      const out = {
        customer: mergeAssignedWorkerName(customerRow),
        pricing: pricingError ? [] : (pricingData || []),
        // Surface errors so the caller can decide whether to flash a toast.
        // We still cache the partial result so subsequent mounts don't refetch.
        error: customerError || pricingError || null,
      };
      if (startedGen === cacheGeneration && startedCustomerGen === (customerGeneration.get(String(customerId)) || 0)) {
        customerCache.set(key, out);
      }
      return out;
    } catch (err) {
      const empty = { customer: null, pricing: [], error: err };
      if (startedGen === cacheGeneration && startedCustomerGen === (customerGeneration.get(String(customerId)) || 0)) {
        customerCache.set(key, empty);
      }
      return empty;
    } finally {
      if (inflightPromises.get(key) === promise) inflightPromises.delete(key);
    }
  })();
  inflightPromises.set(key, promise);
  return promise;
}

/**
 * Fire-and-forget prefetch from a navigation hook (e.g., onNavigateLogJob).
 * Safe to call multiple times — dedups via in-flight Map. Callers here
 * (route-board prefetch: PoolCustomerQuickList, SupervisorJobCard,
 * useWorkerDashboardState) don't know the viewer's price visibility at
 * prefetch time, so `withPricing` defaults from the price-visibility STORE — a
 * genuinely blind mount still resolves correctly via its OWN `loadLogJobCustomer`
 * call (different cache key), just without joining this warm prefetch.
 */
export function prefetchLogJobCustomer({ tenantId, customerId, withPricing, role } = {}) {
  // Default `withPricing` from the STORE, not `true`: the page passes
  // `withPricing = canSeePrices`, so a hard-coded `true` put a blind worker's
  // prefetch on the `:p` key while the page read the `:n` key — the warm never
  // landed AND it burned a customer_pricing query the role cannot read.
  const resolvedWithPricing = withPricing === undefined ? canCurrentSessionSeePrices() : withPricing;
  if (!tenantId || !customerId) return;
  // Don't await — this is fire-and-forget. The actual await happens in
  // useLogJob's effect via loadLogJobCustomer below.
  loadCustomerOnce(tenantId, customerId, resolvedWithPricing, role, false);
}

/**
 * Used by useLogJob's mount effect instead of an inline supabase query.
 * Returns the cached entry if warm, joins an in-flight prefetch otherwise,
 * or fires a fresh fetch on cold cache. `withPricing` should be
 * `usePriceVisibility().canSeePrices` — pass `false` for a price-blind
 * field role so the customer_pricing fetch is skipped (RD-ROLE-005).
 * `role` (from `useAuth()`) decides base vs `field_customers` table
 * (RD-ROLE-005b) — pass it so a blind worker/supervisor mount resolves
 * instead of hitting the base table's 0-row RLS denial.
 */
export function loadLogJobCustomer(tenantId, customerId, { withPricing = true, role, fresh = false } = {}) {
  return loadCustomerOnce(tenantId, customerId, withPricing, role, fresh);
}

// Test-only seam — wipes the cache state without waiting for an auth event.
export function __resetLogJobCustomerCacheForTests() {
  customerCache.clear();
  inflightPromises.clear();
  customerGeneration.clear();
  cacheGeneration += 1;
}

if (supabase?.auth?.onAuthStateChange) {
  supabase.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_OUT' || event === 'SIGNED_IN') {
      customerCache.clear();
      inflightPromises.clear();
      cacheGeneration += 1;
    }
  });
}
